@if (virtualScroll) {
  <cdk-virtual-scroll-viewport
    #viewport
    class="mbs-tree -virtual-scroll"
    [class.-file]="mode === 'file'"
    [itemSize]="virtualItemSize"
    [ngClass]="classesForRoot"
    [style.height]="getVirtualScrollViewportSize()">
    <ng-container *cdkVirtualFor="let item of visibleFlatData; index as index; trackBy: virtualTrackBy">
      <ng-container
        [ngTemplateOutlet]="nodeTemplate"
        [ngTemplateOutletContext]="{ $implicit: item }" />

      @if (!hideLoadMore && item | treeLoadMore: calcHidden : index : visibleFlatData; as loadMoreItem) {
        <div
          class="mbs-tree-item"
          [ngClass]="'-level-' + (loadMoreItem?.level + 1)">
          <ng-container
            [ngTemplateOutlet]="loadMoreTemplate ? loadMoreTemplate : loadMoreButton"
            [ngTemplateOutletContext]="{ $implicit: loadMoreItem }" />
        </div>
      }
    </ng-container>
  </cdk-virtual-scroll-viewport>
} @else {
  <div
    #mbsTree
    class="mbs-tree"
    [class.-file]="mode === 'file'"
    [ngClass]="rootClasses"
    [style.height]="height"
    [style.max-height]="maxHeight"
    [style.min-height]="minHeight">
    @for (item of flatData; track item.node.id ?? item) {
      @if (needToRender(item)) {
        <ng-container
          [ngTemplateOutlet]="nodeTemplate"
          [ngTemplateOutletContext]="{ $implicit: item }" />
      }

      @if (needToRender(item) && !hideLoadMore && item | treeLoadMore: calcHidden : $index : flatData; as loadMoreItem) {
        <div
          class="mbs-tree-item"
          [ngClass]="'-level-' + (loadMoreItem.level + 1)">
          <ng-container
            [ngTemplateOutlet]="loadMoreTemplate ? loadMoreTemplate : loadMoreButton"
            [ngTemplateOutletContext]="{ $implicit: loadMoreItem }" />
        </div>
      }
    }
  </div>
}

<ng-template
  #loadMoreButton
  let-item>
  <mbs-button
    [disabled]="disabledTree || (disableIfLoadingSubtree && loadingSubtree) || isDisabled(item)"
    [icon]="'ico ico-Refresh'"
    [isCtrl]="true"
    [loading]="item.node.loadingChildren || loadingSubtree"
    [type]="'primary'"
    (click)="handleClickLoadMore(item.node?.parent, $event)">
    <span class="nowrap">
      {{ item?.node?.parent?.loadMoreButtonCaption || 'Load more...' }}
    </span>
  </mbs-button>
</ng-template>

<ng-template
  #textLayout
  let-item>
  @if (item.node.icon; as icon) {
    <span
      class="mbs-tree-item_icon"
      [ngClass]="icon"></span>
  }

  <mbs-button
    class="mbs-tree-item_text"
    [class.-hideArrow]="hideTreeIcon"
    [class.hiddenCheckbox]="mode !== 'text' && item.node.hideCheckbox"
    [disabled]="disabledTree || isDisabled(item)"
    [isCtrl]="true"
    [type]="'primary'"
    (click)="handleClickItem(item, $event, false)">
    <ng-container
      [ngTemplateOutlet]="template ? template : defaultLabelTemplate"
      [ngTemplateOutletContext]="{ $implicit: item.node }" />
  </mbs-button>

  @if (appendTemplate; as appendTemplate) {
    <div class="mbs-tree-item_append">
      <ng-container
        [ngTemplateOutlet]="appendTemplate"
        [ngTemplateOutletContext]="{ $implicit: item.node }" />
    </div>
  }
</ng-template>

<ng-template
  #checkboxLayout
  let-item>
  <ng-template #notAllChildrenTooltip>
    <div class="text-start">
      @if (customDisableText && (item?.node?.parent?.totalChildren || 0) <= (item?.node?.parent?.children?.length || 0)) {
        <span>{{ customDisableText }}</span>
      } @else {
        You can change your selection only after all items are loaded. <br />
        Scroll down and click {{ item?.node?.parent?.loadMoreButtonCaption || 'Load more...' }} to complete upload
      }
    </div>
  </ng-template>

  <mbs-checkbox
    class="mbs-tree-item_checkbox"
    [class.-checked]="mode === 'file' && item.node.checked"
    [class.-hideArrow]="hideTreeIcon"
    [class.-indeterminate]="mode === 'file' && item.node.indeterminate"
    [container]="'body'"
    [disabled]="disabledTree || isDisabled(item) || item?.node?.disableCheckbox"
    [disabledSelf]="item?.node?.readonly"
    [disableTooltip]="!item?.node?.disableCheckbox"
    [id]="idPrefix + item.node.id"
    [indeterminate]="item.node.indeterminate"
    [label]="template || item.node.label"
    [labelContext]="item.node"
    [ngbTooltip]="notAllChildrenTooltip"
    [placement]="'top-left'"
    [value]="item.node.value"
    [(ngModel)]="item.node.checked"
    (change)="handleCheck($event, item)" />

  @if (appendTemplate; as appendTemplate) {
    <div class="mbs-tree-item_append">
      <ng-container
        [ngTemplateOutlet]="appendTemplate"
        [ngTemplateOutletContext]="{ $implicit: item.node }" />
    </div>
  }
</ng-template>

<ng-template
  #defaultLabelTemplate
  let-node>
  {{ node.label }}
</ng-template>

<ng-template
  #nodeTemplate
  let-item>
  <div
    class="mbs-tree-item"
    [class.d-none]="itemsCanBeHidden && !item.node.shown"
    [ngClass]="[hashTreeClasses[item?.node?.id]?.levelClass, itemClasses]">
    <div
      class="mbs-tree-item_label"
      [class.-highlighted]="item?.node?.highlighted"
      [ngClass]="[
        hashTreeClasses[item?.node?.id]?.otherClasses,
        selectable &&
        (item?.node?.selected || item?.node?.id === selectedItemId) &&
        !hashTreeClasses[item?.node?.id]?.isDisabled &&
        !disabledTree
          ? selectClass
          : ''
      ]">
      @if (!hideTreeIcon && (item.node.children?.length > 0 || (lazy && !item.node.gotChildren))) {
        <mbs-button
          class="mbs-tree-item_arrow"
          [customClasses]="!item.node.expanded ? '' : '-expanded'"
          [disabled]="disabledTree || (disableIfLoadingSubtree && loadingSubtree) || isDisabled(item)"
          [isCtrl]="true"
          [loading]="item.node.loadingChildren"
          [type]="'dark'"
          (click)="handleClickItem(item, $event, true)" />
      }

      <ng-container
        [ngTemplateOutlet]="mode === 'text' || item.node.hideCheckbox ? textLayout : checkboxLayout"
        [ngTemplateOutletContext]="{ $implicit: item }" />
    </div>
  </div>
</ng-template>
